// Shared with mju-aioe/packages/shared/src/schemas/projectLinks.ts — keep the two identical.
/**
 * The links between a project task and the repository tasks that carry it
 * (docs/plans/2026-10-project-level-work.md), in a file of their own so that
 * both `./projectMap.ts` (a map task carries them) and `./projectWork.ts`
 * (which defines the roll-up over them) can import them without importing
 * each other's values.
 */
import { z } from "zod";
/** The project board's id on the rootless mount. A repository may still have a board of that name. */
export const PROJECT_BOARD_ID = "project";
/** What stands where a repository's id would, for anything that is the project's own. */
export const PROJECT_LEVEL_ROOT_ID = "";
const id = z.string().min(1).max(200);
/** A repository task carrying part of a project task: the repository, its board there, and the task's stable id. */
export const taskDelegateSchema = z.object({ rootId: id, board: id, uid: id });
/** On a repository task: the project task it carries part of. */
export const taskParentSchema = z.object({ projectId: id, board: z.literal(PROJECT_BOARD_ID), uid: id });
/**
 * How a task was closed, as a project's surfaces say it (AIOE 0.34.0, "done means done"): a map task's `closure.kind`
 * is the board task's `closure.reason` (a task that is done with no closure reads `done`). A cancelled part is a
 * blocked task with a cancelled closure; it never counts as done.
 */
export const TASK_CLOSURE_KINDS = ["done", "cancelled", "refused", "handed-on", "blocked"];
export const taskClosureKindSchema = z.enum(TASK_CLOSURE_KINDS);
/**
 * A repository task as the roll-up and the waits read it: `cancelled` for a task closed as cancelled, `handed-on` for one
 * closed as handed on (counted under `other`, never `done`: its waiters keep waiting), else its status. Only `done` (status
 * done with no closure, or a `done` closure) releases a wait or counts as done.
 */
export function partStatusOf(task) {
    const reason = task.closure?.reason;
    if (reason === "cancelled")
        return "cancelled";
    if (reason === "handed-on")
        return "handed-on";
    return task.status;
}
export const TASK_ROLLUP_STATUSES = ["blocked", "in-progress", "review", "done", "not-started"];
export const taskRollupSchema = z.object({
    status: z.enum(TASK_ROLLUP_STATUSES),
    counts: z.object({ done: z.number().int().nonnegative(), review: z.number().int().nonnegative(), "in-progress": z.number().int().nonnegative(), blocked: z.number().int().nonnegative(), other: z.number().int().nonnegative(), /** Parts cancelled (AIOE 0.34.0); a roll-up written before it reads 0. */ cancelled: z.number().int().nonnegative().default(0) }),
    /** `part_cancelled` when a cancelled part holds the roll-up at blocked: the project task is never done while a part was cancelled. */
    reason: z.literal("part_cancelled").optional(),
    /** Delegates named whose task is not there (deleted, or on a board not known). */
    missing: z.number().int().nonnegative(),
    at: z.string().max(50),
});
//# sourceMappingURL=projectLinks.js.map